import axios from 'axios';
import { API, setAccessToken } from '../utils/api.utils';
import api from '../utils/api.utils';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { tokenManager } from './tokenManager';
import { logAuthEvent } from '../utils/sentry';

export interface FirebaseAuthRequest {
  id_token: string;
  name: string;
  email: string;
  photoURL: string;
}

export interface FirebaseAuthResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  user?: any;
  is_new_user?: boolean;
}

export interface BackendJWTResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  user?: any;
  is_new_user?: boolean;
}

export interface BootstrapAuthResponse {
  user: any | null;
}

// Bootstrap authentication on app startup
export async function bootstrapAuth(): Promise<BootstrapAuthResponse> {
  try {
    logAuthEvent('bootstrap_auth_start');
    
    // ✅ Create a plain axios instance without interceptors
    const plainApi = axios.create({
      baseURL: API.BASE_URL,
      withCredentials: true, // important: send refresh token cookie
      timeout: 10000,
    });

    // 1. Try to refresh access token
    const response = await plainApi.post(API.REFRESH_TOKEN);
    const data = response.data as BackendJWTResponse;

    // Use token manager for proper token handling
    tokenManager.setToken(data.access_token);

    // 2. Get user profile (use your normal api instance here if you want)
    const userResponse = await plainApi.get(API.ME, {
      headers: { Authorization: `Bearer ${data.access_token}` },
    });

    logAuthEvent('bootstrap_auth_success', { hasUser: !!userResponse.data });
    return { user: userResponse.data };
  } catch (error: any) {
    logAuthEvent('bootstrap_auth_failed', { 
      error: error.response?.data?.message,
      status: error.response?.status 
    });
    
    // If refresh fails, user is not authenticated
    tokenManager.setToken(null);
    return { user: null };
  }
}

// Login with Firebase
export const notifyBackendOfFirebaseAuth = async (
  idToken: string,
  name: string,
  email: string,
  photoURL: string
): Promise<BackendJWTResponse> => {
  logAuthEvent('firebase_auth_start', { email });
  
  const data = await api.post(API.FIREBASE_AUTH, {
    id_token: idToken,
    name,
    email,
    photoURL,
  } as FirebaseAuthRequest);

  // Use token manager for proper token handling
  tokenManager.setToken(data.data.access_token);
  
  try {
    const plainApi = axios.create({
      baseURL: API.BASE_URL,
      withCredentials: true,
      timeout: 10000,
      headers: { Authorization: `Bearer ${data.data.access_token}` },
    });
    const meRes = await plainApi.get(API.ME);
    data.data.user = meRes.data;
  } catch (e) {
  }
  
  logAuthEvent('firebase_auth_success', { 
    isNewUser: data.data.is_new_user,
    hasAccessToken: !!data.data.access_token 
  });
  
  return data.data;
};

// Logout function
export const logout = async (): Promise<void> => {
  try {
    logAuthEvent('logout_start');
    await api.post(API.LOGOUT);
    logAuthEvent('logout_backend_success');
  } catch (error: any) {
    logAuthEvent('logout_backend_failed', { error: error.message });
    // Backend logout notification failed, continue with cleanup
  } finally {
    // Clean up token manager
    tokenManager.destroy();
    setAccessToken(null);
    localStorage.removeItem('cached_user');
    localStorage.removeItem('onboarding_current_step');
    localStorage.removeItem('onboarding_data');
    localStorage.removeItem('onboarding_user_email');
    
    // Sign out from Firebase
    const { signOutUser } = await import('../utils/firebase.utils');
    await signOutUser();
    logAuthEvent('logout_completed');
  }
};